import { FC, useEffect, useReducer, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Bottomsheet from '@ixigo/iui/Bottomsheet';
import { InfoCircle, Close } from '@ixigo/icons';
import IconButton from '@ixigo/iui/IconButton';
import Typography from '@ixigo/iui/Typography';
import ActionSheet from '../../../components/AiSmartSupport/ActionSheet';
import Header from '../../../components/AiSmartSupport/Header';
import Summary from '../../../components/AiSmartSupport/Summary';
import TimelineSection from '../../../components/AiSmartSupport/Timeline/TimelineSection';
import TimelineEntryDetails from '../../../components/AiSmartSupport/Details/TimelineEntryDetails';
import BotSessionDetails from '../../../components/AiSmartSupport/Details/BotSessionDetails';
import CallHistoryDetails from '../../../components/AiSmartSupport/Details/CallHistoryDetails';
import BotSessionCard from '../../../components/AiSmartSupport/ChatConversationCard';
import CallHistoryCard from '../../../components/AiSmartSupport/CallHistoryCard';
import SmartSupportLoader from '../../../components/AiSmartSupport/SmartSupportLoader';
import {
    HistoryItem,
    TimelineData,
    BotSession,
    CallHistoryItem,
    BookingDetail,
    LobPhoneNumbers,
} from '../../../components/AiSmartSupport/timelineTypes';
import { getTimelineData } from '../../../apis/aiSmartSupport';
import { AiSmartSupport } from '../../../enums/booking';
import { useToast } from '../../../contexts/ToastContext';
import FullScreenError from '../../../components/Error/FullScreen';
import useUserInfo from '../../../hooks/useUserInfo';

// Simple state interface
interface State {
    bookingData: TimelineData | null;
    bookingDetail: BookingDetail | null;
    lobPhoneNumbers: LobPhoneNumbers | null;
    botSessions: BotSession[];
    callHistory: CallHistoryItem[];
    showEscalation: boolean;
    callTitle?: string;
    callSubTitle?: string;
    loading: boolean;
    error: boolean;
    showAiSummaryLabel: boolean;
    modals: {
        contactSheetOpen: boolean;
        historyDetailOpen: boolean;
        selectedHistoryItem: HistoryItem | null;
        botSessionDetailOpen: boolean;
        selectedBotSession: BotSession | null;
        callHistoryDetailOpen: boolean;
        selectedCallHistory: CallHistoryItem | null;
    };
}

// Action types
type Action =
    | { type: AiSmartSupport.LOADING_START }
    | {
          type: AiSmartSupport.LOADING_SUCCESS;
          payload: {
              timeline: TimelineData;
              botSessions?: BotSession[];
              callHistory?: CallHistoryItem[];
              bookingDetail?: BookingDetail;
              showEscalation?: boolean;
              lobPhoneNumbers?: LobPhoneNumbers;
              callTitle?: string;
              callSubTitle?: string;
          };
      }
    | { type: AiSmartSupport.LOADING_ERROR }
    | { type: AiSmartSupport.OPEN_CONTACT_SHEET }
    | { type: AiSmartSupport.CLOSE_CONTACT_SHEET }
    | { type: AiSmartSupport.OPEN_HISTORY_DETAIL; payload: HistoryItem }
    | { type: AiSmartSupport.CLOSE_HISTORY_DETAIL }
    | { type: AiSmartSupport.OPEN_BOT_SESSION_DETAIL; payload: BotSession }
    | { type: AiSmartSupport.CLOSE_BOT_SESSION_DETAIL }
    | { type: AiSmartSupport.OPEN_CALL_HISTORY_DETAIL; payload: CallHistoryItem }
    | { type: AiSmartSupport.CLOSE_CALL_HISTORY_DETAIL }
    | { type: AiSmartSupport.HIDE_AI_SUMMARY_LABEL };

// Initial state
const initialState: State = {
    bookingData: null,
    bookingDetail: null,
    lobPhoneNumbers: null,
    botSessions: [],
    callHistory: [],
    showEscalation: false,
    callTitle: undefined,
    callSubTitle: undefined,
    loading: true,
    error: false,
    showAiSummaryLabel: true,
    modals: {
        contactSheetOpen: false,
        historyDetailOpen: false,
        selectedHistoryItem: null,
        botSessionDetailOpen: false,
        selectedBotSession: null,
        callHistoryDetailOpen: false,
        selectedCallHistory: null,
    },
};

// Simple reducer
const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case AiSmartSupport.LOADING_START:
            return { ...state, loading: true, error: false };
        case AiSmartSupport.LOADING_SUCCESS:
            return {
                ...state,
                loading: false,
                bookingData: action.payload.timeline,
                bookingDetail: action.payload.bookingDetail || null,
                lobPhoneNumbers: action.payload.lobPhoneNumbers || null,
                botSessions: action.payload.botSessions || [],
                callHistory: action.payload.callHistory || [],
                showEscalation: action.payload.showEscalation || false,
                callTitle: action.payload.callTitle,
                callSubTitle: action.payload.callSubTitle,
            };
        case AiSmartSupport.LOADING_ERROR:
            return { ...state, loading: false, error: true };
        case AiSmartSupport.OPEN_CONTACT_SHEET:
            return { ...state, modals: { ...state.modals, contactSheetOpen: true } };
        case AiSmartSupport.CLOSE_CONTACT_SHEET:
            return { ...state, modals: { ...state.modals, contactSheetOpen: false } };
        case AiSmartSupport.OPEN_HISTORY_DETAIL:
            return {
                ...state,
                modals: {
                    ...state.modals,
                    historyDetailOpen: true,
                    selectedHistoryItem: action.payload,
                },
            };
        case AiSmartSupport.CLOSE_HISTORY_DETAIL:
            return {
                ...state,
                modals: {
                    ...state.modals,
                    historyDetailOpen: false,
                    selectedHistoryItem: null,
                },
            };
        case AiSmartSupport.OPEN_BOT_SESSION_DETAIL:
            return {
                ...state,
                modals: {
                    ...state.modals,
                    botSessionDetailOpen: true,
                    selectedBotSession: action.payload,
                },
            };
        case AiSmartSupport.CLOSE_BOT_SESSION_DETAIL:
            return {
                ...state,
                modals: {
                    ...state.modals,
                    botSessionDetailOpen: false,
                    selectedBotSession: null,
                },
            };
        case AiSmartSupport.OPEN_CALL_HISTORY_DETAIL:
            return {
                ...state,
                modals: {
                    ...state.modals,
                    callHistoryDetailOpen: true,
                    selectedCallHistory: action.payload,
                },
            };
        case AiSmartSupport.CLOSE_CALL_HISTORY_DETAIL:
            return {
                ...state,
                modals: {
                    ...state.modals,
                    callHistoryDetailOpen: false,
                    selectedCallHistory: null,
                },
            };
        case AiSmartSupport.HIDE_AI_SUMMARY_LABEL:
            return {
                ...state,
                showAiSummaryLabel: false,
            };
        default:
            return state;
    }
};

// Custom hook for booking data
const useBookingData = (bookingId: string) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { userInfo, isUserLoggedIn } = useUserInfo();

    // Load booking data
    useEffect(() => {
        const loadData = async () => {
            // Don't make API call if user is not logged in or user ID is not available
            if (!isUserLoggedIn || !userInfo.uid) {
                dispatch({ type: AiSmartSupport.LOADING_ERROR });
                return;
            }
            try {
                dispatch({ type: AiSmartSupport.LOADING_START });

                const data = await getTimelineData({
                    userId: userInfo.uid,
                    bookingId,
                    cache: true,
                });

                dispatch({
                    type: AiSmartSupport.LOADING_SUCCESS,
                    payload: {
                        timeline: data.timeline,
                        botSessions: data.conversation?.botSessions || [],
                        callHistory: data.callHistory || [],
                        bookingDetail: data.bookingDetail,
                        showEscalation: data.showEscalation,
                        lobPhoneNumbers: data.lobPhoneNumbers || undefined,
                        callTitle: data.callTitle,
                        callSubTitle: data.callSubTitle,
                    },
                });
            } catch (error) {
                dispatch({ type: AiSmartSupport.LOADING_ERROR });
            }
        };

        loadData();
    }, [bookingId, isUserLoggedIn, userInfo.uid]);

    return { state, dispatch };
};

// Main component
const BookingHistory: FC = () => {
    const navigate = useNavigate();
    const { bookingid: bookingId = '' } = useParams();
    const { state, dispatch } = useBookingData(bookingId);
    const { showToast } = useToast();
    const { isUserLoggedIn, userInfo } = useUserInfo();

    // Event handlers
    const handleBack = useCallback(() => navigate(-1), [navigate]);
    const handleContactSupport = useCallback(() => dispatch({ type: AiSmartSupport.OPEN_CONTACT_SHEET }), [dispatch]);
    const handleCloseContactSheet = useCallback(
        () => dispatch({ type: AiSmartSupport.CLOSE_CONTACT_SHEET }),
        [dispatch],
    );
    const handleShowToast = useCallback((message: string, icon: string) => showToast(message, icon), [showToast]);
    const handleHistoryItemClick = useCallback(
        (item: HistoryItem) => dispatch({ type: AiSmartSupport.OPEN_HISTORY_DETAIL, payload: item }),
        [dispatch],
    );
    const handleCloseHistoryDetail = useCallback(
        () => dispatch({ type: AiSmartSupport.CLOSE_HISTORY_DETAIL }),
        [dispatch],
    );

    const handleBotSessionClick = useCallback(
        (session: BotSession) => dispatch({ type: AiSmartSupport.OPEN_BOT_SESSION_DETAIL, payload: session }),
        [dispatch],
    );

    const handleCloseBotSessionDetail = useCallback(
        () => dispatch({ type: AiSmartSupport.CLOSE_BOT_SESSION_DETAIL }),
        [dispatch],
    );

    const handleCallHistoryClick = useCallback(
        (call: CallHistoryItem) => dispatch({ type: AiSmartSupport.OPEN_CALL_HISTORY_DETAIL, payload: call }),
        [dispatch],
    );

    const handleCloseCallHistoryDetail = useCallback(
        () => dispatch({ type: AiSmartSupport.CLOSE_CALL_HISTORY_DETAIL }),
        [dispatch],
    );

    const handleHideAiSummaryLabel = useCallback(
        () => dispatch({ type: AiSmartSupport.HIDE_AI_SUMMARY_LABEL }),
        [dispatch],
    );

    // Calculate if we have valid phone numbers for the specific product type
    const getValidPhoneNumber = useCallback((): string => {
        if (!state.bookingDetail?.product || !state.lobPhoneNumbers) return '';

        const product = state.bookingDetail.product.toLowerCase();

        if (
            product.includes('flight') &&
            state.lobPhoneNumbers.flights.active &&
            state.lobPhoneNumbers.flights.phoneNumber
        ) {
            return state.lobPhoneNumbers.flights.phoneNumber;
        }
        if (
            product.includes('hotel') &&
            state.lobPhoneNumbers.hotels.active &&
            state.lobPhoneNumbers.hotels.phoneNumber
        ) {
            return state.lobPhoneNumbers.hotels.phoneNumber;
        }
        if (
            product.includes('train') &&
            state.lobPhoneNumbers.trains.active &&
            state.lobPhoneNumbers.trains.phoneNumber
        ) {
            return state.lobPhoneNumbers.trains.phoneNumber;
        }

        if (product.includes('bus') && state.lobPhoneNumbers.bus.active && state.lobPhoneNumbers.bus.phoneNumber) {
            return state.lobPhoneNumbers.bus.phoneNumber;
        }
        return '';
    }, [state.bookingDetail?.product, state.lobPhoneNumbers]);

    // Show error if user is not logged in or userId is missing
    if (!isUserLoggedIn || !userInfo.uid) {
        return <FullScreenError />;
    }

    // Loading state
    if (state.loading) {
        return <SmartSupportLoader />;
    }

    // Error state
    if (state.error) {
        return <FullScreenError />;
    }

    // No data state
    if (!state.bookingData) {
        return <FullScreenError />;
    }

    return (
        <div className='flex flex-col min-h-full bg-white'>
            {/* Header */}
            <div className='sticky top-0 z-20 bg-white'>
                <Header
                    bookingDetails={state.bookingDetail || {}}
                    onBack={handleBack}
                    onContactSupport={handleContactSupport}
                    showEscalation={state.showEscalation}
                />
            </div>

            {/* AI Summary Label */}
            {state.showAiSummaryLabel && (
                <div className='flex items-center justify-between px-15 py-5 bg-white border-b border-zinc-100 h-[30px] max-h-[30px] shadow-[0px_4px_20px_0px_rgba(30,41,59,0.3)]'>
                    <div className='flex items-center gap-10'>
                        <InfoCircle fontSize={16} className='text-zinc-900' />
                        <Typography variant='body-sm' className='text-zinc-900'>
                            AI-generated summary.
                        </Typography>
                    </div>
                    <IconButton
                        variant='flat'
                        color='neutral'
                        size='sm'
                        onClick={handleHideAiSummaryLabel}
                        className='!p-5'
                    >
                        <Close fontSize={16} className='text-zinc-900' />
                    </IconButton>
                </div>
            )}

            {/* Content */}
            <div className='flex-1 overflow-y-auto p-15 mt-20'>
                <Summary summary={state.bookingData.summary} bookingId={bookingId} userId={userInfo.uid} />
                <div className='mt-20'>
                    {state.bookingData.categorizedTimeline.map((section) => (
                        <TimelineSection
                            key={section.category}
                            section={{
                                title: section.category,
                                status: section.status.toLowerCase(),
                                items: section.items.map((item) => ({
                                    id: item.id,
                                    type: item.type,
                                    title: item.title,
                                    date: item.timestamp,
                                    summary: item.details.threadSummary,
                                    status: item.details.status,
                                    details: {
                                        threadResolution: item.details.threadResolution,
                                        threadStatus: item.details.threadStatus,
                                    },
                                })),
                            }}
                            onItemClick={handleHistoryItemClick}
                        />
                    ))}

                    {/* Bot Sessions Section */}
                    {state.botSessions && state.botSessions.length > 0 && (
                        <div className='mt-20'>
                            <BotSessionCard sessions={state.botSessions} onSessionClick={handleBotSessionClick} />
                        </div>
                    )}

                    {/* Call History Section */}
                    {state.callHistory && state.callHistory.length > 0 && (
                        <div className='mt-20'>
                            <CallHistoryCard callHistory={state.callHistory} onCallClick={handleCallHistoryClick} />
                        </div>
                    )}
                </div>
            </div>

            {/* Contact Sheet Modal */}
            <Bottomsheet
                open={state.modals.contactSheetOpen}
                onClose={handleCloseContactSheet}
                classes={{ container: '!min-w-full !max-h-[227px]' }}
            >
                <ActionSheet
                    onClose={handleCloseContactSheet}
                    onShowToast={handleShowToast}
                    bookingDetail={state.bookingDetail || undefined}
                    timelineData={state.bookingData || undefined}
                    botSessions={state.botSessions}
                    callHistory={state.callHistory}
                    callTitle={state.callTitle}
                    callSubTitle={state.callSubTitle}
                    getValidPhoneNumber={getValidPhoneNumber}
                />
            </Bottomsheet>

            {/* History Detail Modal */}
            <TimelineEntryDetails
                item={state.modals.selectedHistoryItem}
                isOpen={state.modals.historyDetailOpen}
                onClose={handleCloseHistoryDetail}
                resolutionMessage={state.modals.selectedHistoryItem?.details?.threadResolution}
                showThreadResolution={!!state.modals.selectedHistoryItem?.details?.threadResolution}
            />

            {/* Bot Session Detail Modal */}
            <BotSessionDetails
                session={state.modals.selectedBotSession}
                isOpen={state.modals.botSessionDetailOpen}
                onClose={handleCloseBotSessionDetail}
            />

            {/* Call History Detail Modal */}
            <CallHistoryDetails
                call={state.modals.selectedCallHistory}
                isOpen={state.modals.callHistoryDetailOpen}
                onClose={handleCloseCallHistoryDetail}
            />
        </div>
    );
};

export default BookingHistory;
